import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  ArrowsLeftRight,
  Buildings,
  Calendar,
  ChartLine,
  CheckCircle,
  ClipboardText,
  CurrencyDollar,
  GearSix,
  Handshake,
  Lightning,
  Lock,
  Megaphone,
  Question,
  Quotes,
  Receipt,
  Repeat,
  Star,
  Target,
  TrendUp,
  UsersThree,
  X
} from '@phosphor-icons/react';

const ConsultantLanding = () => {
  const [clients, setClients] = useState(10);
  const [fee, setFee] = useState(1500);
  const monthlyRevenue = clients * fee;

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  return (
    <div className="bg-[#161B26] min-h-screen">
      <SEO 
        title="Deploy AI Automations for Clients | Man & Machine"
        description="Help service businesses capture every lead, respond instantly, and book more jobs. Deploy AI workers for your clients in minutes."
        canonicalUrl="https://manmachine.agency/consultants"
      />
      <LandingHeader />

      {/* ══════════════════════════════════════════════════════════════════════
          HERO SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="pt-28 pb-20 relative overflow-hidden min-h-[80vh] flex items-center">
        {/* Background image - subject on right */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_0553c8fb-e46a-4d53-a226-9746faedb587/artifacts/cqhunfp2_Consultant-img1.png"
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.5, filter: 'saturate(0.6)', objectPosition: '70% center', transform: 'scale(0.7)', transformOrigin: 'right center' }}
          />
          <div className="absolute inset-0" style={{
            background: `
              linear-gradient(to right, #161B26 0%, rgba(22,27,38,0.85) 40%, rgba(22,27,38,0.4) 65%, rgba(22,27,38,0.2) 100%),
              linear-gradient(to bottom, #161B26 0%, transparent 15%, transparent 80%, #161B26 100%)
            `
          }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <div className="max-w-xl mr-auto ml-[5%]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-left"
            >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
              <Lightning size={14} className="text-[#00E5C0]" />
              <span className="text-xs text-[#A0AEC0]">For Agencies and Consultants</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.12] mb-5">
              Leads, Jobs, and Follow-Ups<br />
              <span className="text-[#00E5C0]">Handled Automatically for Your Clients.</span>
            </h1>

            <p className="text-lg text-[#A0AEC0] mb-3 leading-relaxed max-w-2xl mx-auto">
              Help service businesses capture every lead, respond instantly, and book more jobs - without adding admin or hiring staff.
            </p>

            <p className="text-sm text-[#7D8896] mb-10">
              Deploy AI workers for your clients in minutes. No complex builds. No ongoing setup.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
              >
                Start Building for Clients
                <ArrowRight size={18} />
              </Link>
              <a
                href="#approach"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
              >
                See How It Works
              </a>
            </div>
            
            <div className="mt-2.5">
              <Link
                to="/login"
                className="text-[#00E5C0] hover:text-[#33EDCF] text-sm font-medium transition-colors"
              >
                Already have an account? Sign in
                <ArrowRight size={12} className="inline ml-1" />
              </Link>
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          IDEAL FOR SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 border border-[rgba(0,229,192,0.15)]"
          >
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <CheckCircle size={22} className="text-[#10B981]" />
              Ideal For
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "AI consultants delivering automation for service businesses",
                "Agencies setting up lead capture and follow-up systems",
                "Consultants building repeatable systems across clients",
                "Advisors helping businesses stop missing leads and save time"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-[#E2E8F0]">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]" />
                  {item}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          BUILT FOR AGENCIES SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10">
              Built for Agencies Delivering AI to{' '}
              <span className="text-[#00E5C0]">Service Businesses</span>
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {[
                { icon: Lightning, text: "Launch AI services without building custom systems" },
                { icon: Target, text: "Deliver real outcomes (leads, bookings, follow-ups)" },
                { icon: GearSix, text: "Standardise your delivery across clients" },
                { icon: ChartLine, text: "Scale without adding headcount" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 glass rounded-xl">
                  <item.icon size={20} className="text-[#00E5C0] flex-shrink-0" />
                  <p className="text-[#E2E8F0] font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          HOW YOU DELIVER RESULTS SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="approach" className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How You Deliver{' '}
              <span className="text-[#00E5C0]">Results for Clients</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "1", title: "Connect their business", desc: "Website, inbox, calendar", color: "#00E5C0" },
              { num: "2", title: "Activate AI workers", desc: "For leads, bookings, and follow-ups", color: "#EC4899" },
              { num: "3", title: "Let the system run", desc: "Automatically in the background", color: "#10B981" },
              { num: "4", title: "Track results and expand", desc: "Into more workflows", color: "#F59E0B" }
            ].map((step, idx) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
              >
                <div className="glass rounded-2xl p-6 h-full border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.3)] transition-colors">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${step.color}20` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: step.color }}>{step.num}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-[#A0AEC0]">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          EVERYTHING YOU NEED SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to{' '}
              <span className="text-[#00E5C0]">Deliver AI for Clients</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: ClipboardText, title: "Pre-built workers", desc: "For service businesses" },
              { icon: Buildings, title: "Multi-client management", desc: "Partner Pro" },
              { icon: Lightning, title: "Fast deployment", desc: "Minimal setup required" },
              { icon: GearSix, title: "Built-in integrations", desc: "Email, calendar, CRM" },
              { icon: ChartLine, title: "Scalable delivery model", desc: "Grow without friction" }
            ].map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.08 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)]"
              >
                <div className="w-12 h-12 rounded-xl bg-[rgba(0,229,192,0.1)] flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-[#00E5C0]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-[#A0AEC0]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHAT YOU CAN DELIVER SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
              What You Can Deliver to{' '}
              <span className="text-[#00E5C0]">Your Clients</span>
            </h2>

            <div className="space-y-3 text-left max-w-lg mx-auto mb-8">
              {[
                "Every lead responded to instantly",
                "More jobs booked without extra staff",
                "Follow-ups handled automatically",
                "Reduced admin workload",
                "Clear ROI from day one"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 glass rounded-xl">
                  <CheckCircle size={18} className="text-[#10B981] flex-shrink-0" />
                  <span className="text-[#E2E8F0]">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-lg text-white font-medium p-4 glass rounded-xl border-l-4 border-[#00E5C0]">
              Turn AI into a real, billable service for your clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SIMPLE DELIVERY MODEL SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Simple, Repeatable{' '}
              <span className="text-[#00E5C0]">Delivery Model</span>
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Onboard client",
              "Connect tools",
              "Activate workers",
              "Monitor and optimise",
              "Expand into additional workflows"
            ].map((step, idx) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.08 }}
                className="flex items-center gap-3"
              >
                <div className="glass rounded-2xl px-5 py-4 text-center min-w-[140px]">
                  <div className="w-8 h-8 rounded-full bg-[#00E5C0] flex items-center justify-center mx-auto mb-2">
                    <span className="text-[#0A0F14] font-bold text-sm">{idx + 1}</span>
                  </div>
                  <p className="text-sm font-medium text-white">{step}</p>
                </div>
                {idx < 4 && (
                  <ArrowRight size={18} className="text-[#00E5C0] hidden sm:block" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          DONE FOR YOU vs AI-ENABLED AGENCY SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              From Done-For-You to{' '}
              <span className="text-[#00E5C0]">Scalable AI Delivery</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Traditional */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass rounded-2xl p-7 border border-[rgba(255,255,255,0.06)]"
            >
              <h3 className="text-lg font-semibold text-[#A0AEC0] mb-5">Traditional Agency Model</h3>
              <div className="space-y-3.5">
                {[
                  'Custom builds for every client',
                  'High time per project',
                  'Limited scalability',
                  'Requires more staff to grow',
                  'Inconsistent delivery'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <X size={16} weight="bold" className="text-[#EF4444] flex-shrink-0" />
                    <span className="text-[#A0AEC0] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* AI-Enabled */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass rounded-2xl p-7 border border-[rgba(0,229,192,0.2)]"
            >
              <h3 className="text-lg font-semibold text-[#00E5C0] mb-5">AI-Enabled Agency Model</h3>
              <div className="space-y-3.5">
                {[
                  'Reusable systems across clients',
                  'Fast deployment',
                  'Scales without hiring',
                  'Predictable delivery model',
                  'Higher margins'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={16} weight="fill" className="text-[#00E5C0] flex-shrink-0" />
                    <span className="text-[#E2E8F0] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Supporting line (Prompt 1) + Low Effort Scaling reinforcement (Prompt 5) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="mt-10 text-center space-y-3"
          >
            <p className="text-white font-medium text-lg">
              Shift from trading time for money to building scalable systems.
            </p>
            <p className="text-[#A0AEC0] text-sm max-w-2xl mx-auto">
              With AI-enabled delivery, you are not increasing effort linearly with revenue. You are building systems once and reusing them across clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PROFIT CALCULATOR SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              See What This Could Look Like{' '}
              <span className="text-[#00E5C0]">for Your Agency</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 border border-[rgba(0,229,192,0.15)]"
          >
            {/* Inputs */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="text-sm text-[#A0AEC0] mb-2 block">Number of clients</label>
                <div className="flex items-center gap-2">
                  {[5, 10, 20].map(val => (
                    <button
                      key={val}
                      onClick={() => setClients(val)}
                      data-testid={`calc-clients-${val}`}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        clients === val
                          ? 'bg-[#00E5C0] text-[#0A0F14]'
                          : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.08)]'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-[#A0AEC0] mb-2 block">Average monthly client fee</label>
                <div className="flex items-center gap-2">
                  {[1000, 1500, 2000].map(val => (
                    <button
                      key={val}
                      onClick={() => setFee(val)}
                      data-testid={`calc-fee-${val}`}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        fee === val
                          ? 'bg-[#00E5C0] text-[#0A0F14]'
                          : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.08)]'
                      }`}
                    >
                      ${val.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="border-t border-[rgba(0,229,192,0.15)] pt-6">
              <div className="flex items-center justify-center gap-4 flex-wrap text-center">
                <div>
                  <p className="text-[#A0AEC0] text-xs mb-1">Clients</p>
                  <p className="text-white text-2xl font-bold">{clients}</p>
                </div>
                <span className="text-[#7D8896] text-lg">&times;</span>
                <div>
                  <p className="text-[#A0AEC0] text-xs mb-1">Monthly fee</p>
                  <p className="text-white text-2xl font-bold">${fee.toLocaleString()}</p>
                </div>
                <span className="text-[#7D8896] text-lg">=</span>
                <div className="bg-[rgba(0,229,192,0.08)] rounded-xl px-6 py-3 border border-[rgba(0,229,192,0.2)]">
                  <p className="text-[#A0AEC0] text-xs mb-1">Monthly revenue</p>
                  <p data-testid="calc-result" className="text-[#00E5C0] text-3xl font-bold">${monthlyRevenue.toLocaleString()}/mo</p>
                </div>
              </div>
            </div>

            {/* Supporting line (Prompt 5) */}
            <p className="text-center text-sm text-[#7D8896] mt-6">
              Scale your agency revenue without increasing your workload.
            </p>
          </motion.div>

          {/* Psychological framing (Prompt 4) */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.3 }}
            className="mt-8 text-center space-y-2"
          >
            <p className="text-white font-medium">
              Most agencies underestimate how quickly this scales.
            </p>
            <p className="text-[#A0AEC0] text-sm">
              Even a small number of clients can generate significant recurring revenue.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          GET YOUR FIRST 3 CLIENTS SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Get Your First{' '}
              <span className="text-[#00E5C0]">3 Clients</span>
            </h2>
            <p className="text-[#A0AEC0]">
              Start simple. Prove results. Then scale.
            </p>
          </motion.div>

          <div className="space-y-5">
            {[
              {
                num: '1',
                title: 'Choose one simple offer',
                desc: 'Focus on a single outcome like "Never Miss a Lead" or "Automated Booking"'
              },
              {
                num: '2',
                title: 'Target one service niche',
                desc: 'Pick a niche like HVAC, cleaning, plumbing, or electrical'
              },
              {
                num: '3',
                title: 'Deploy the same system for each client',
                desc: 'Use the same setup, refine it, and reuse it'
              }
            ].map((step, idx) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="flex items-start gap-5"
              >
                <div className="w-9 h-9 rounded-full bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#00E5C0] font-bold text-sm">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{step.title}</h3>
                  <p className="text-sm text-[#A0AEC0]">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.3 }}
            className="mt-10 text-center space-y-4"
          >
            <p className="text-sm text-[#7D8896]">
              You don't need a complex system to start - just one that works.
            </p>
            <p className="text-white font-medium text-sm">
              Most agencies overcomplicate this - the fastest way to grow is to start with one simple offer and repeat it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          AGENCY PRICING & REVENUE SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
              <CurrencyDollar size={14} className="text-[#00E5C0]" />
              <span className="text-xs text-[#A0AEC0]">Agency Revenue Model</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How Agencies Price{' '}
              <span className="text-[#00E5C0]">AI Services</span>
            </h2>
            <p className="text-[#A0AEC0] max-w-2xl mx-auto">
              Your clients pay for outcomes, not technology. Here is how top agencies structure their pricing.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                label: 'Setup Fee',
                price: '$500 - $2,000',
                desc: 'One-time onboarding, connecting tools, and configuring workers for each client.',
                color: '#00E5C0'
              },
              {
                label: 'Monthly Retainer',
                price: '$300 - $1,500/mo',
                desc: 'Ongoing management, monitoring, and optimisation of their AI workers.',
                color: '#EC4899'
              },
              {
                label: 'Per-Result Fee',
                price: '$5 - $50/lead',
                desc: 'Charge per booked appointment, qualified lead, or completed follow-up.',
                color: '#F59E0B'
              }
            ].map((tier, idx) => (
              <motion.div
                key={tier.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.3)] transition-colors text-center"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${tier.color}15` }}
                >
                  <CurrencyDollar size={24} style={{ color: tier.color }} />
                </div>
                <p className="text-sm text-[#A0AEC0] mb-1">{tier.label}</p>
                <p className="text-2xl font-bold text-white mb-3">{tier.price}</p>
                <p className="text-sm text-[#7D8896]">{tier.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.3 }}
            className="text-center text-sm text-[#7D8896] mt-8"
          >
            Most agencies combine a setup fee with a monthly retainer. Your margins stay high because delivery is automated.
          </motion.p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          REVENUE EXAMPLE SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Revenue Example:{' '}
              <span className="text-[#00E5C0]">10 Clients</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 border border-[rgba(0,229,192,0.15)]"
          >
            <div className="space-y-5">
              {[
                { label: 'Setup fees (10 clients x $1,000)', value: '$10,000', sub: 'one-time' },
                { label: 'Monthly retainers (10 x $750/mo)', value: '$7,500', sub: '/month' },
                { label: 'Platform cost', value: '-$299', sub: '/month' }
              ].map((row, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 border-b border-[rgba(255,255,255,0.06)]">
                  <span className="text-[#E2E8F0]">{row.label}</span>
                  <div className="text-right">
                    <span className="text-white font-semibold text-lg">{row.value}</span>
                    <span className="text-[#7D8896] text-xs ml-2">{row.sub}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[rgba(0,229,192,0.2)] flex items-center justify-between">
              <div>
                <p className="text-[#A0AEC0] text-sm">Recurring monthly revenue</p>
                <p className="text-3xl font-bold text-[#00E5C0]">$7,201/mo</p>
              </div>
              <div className="text-right">
                <p className="text-[#A0AEC0] text-sm">First-year projection</p>
                <p className="text-3xl font-bold text-white">$96,412</p>
              </div>
            </div>

            <p className="text-xs text-[#7D8896] mt-4 text-center">
              Based on 10 service-business clients. Your actual revenue depends on pricing, client mix, and add-on services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHAT YOU CAN SELL SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What You Can{' '}
              <span className="text-[#00E5C0]">Sell to Clients</span>
            </h2>
            <p className="text-[#A0AEC0] max-w-2xl mx-auto">
              Package these as standalone services or bundle them into monthly retainers.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: Target,
                title: 'Lead Capture System',
                desc: 'Never miss an enquiry. Every website visitor, missed call, and email gets an instant response.',
                tag: '$500 - $1,500 setup'
              },
              {
                icon: Calendar,
                title: 'Booking Automation',
                desc: 'Leads go straight to the calendar. No back-and-forth, no missed appointments.',
                tag: '$400 - $1,200 setup'
              },
              {
                icon: Lightning,
                title: 'Follow-Up Engine',
                desc: 'Automated sequences that chase quotes, re-engage cold leads, and recover lost jobs.',
                tag: '$300 - $800 setup'
              },
              {
                icon: Star,
                title: 'Review Collection',
                desc: 'Post-job review requests sent automatically. Build reputation without lifting a finger.',
                tag: '$200 - $500 setup'
              },
              {
                icon: Receipt,
                title: 'Invoice Follow-Up',
                desc: 'Polite, persistent payment reminders. Reduce overdue invoices without awkward conversations.',
                tag: '$300 - $700 setup'
              },
              {
                icon: UsersThree,
                title: 'Customer Reactivation',
                desc: 'Re-engage past clients with timely check-ins and seasonal offers.',
                tag: '$300 - $600 setup'
              }
            ].map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.08 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0">
                    <service.icon size={20} className="text-[#00E5C0]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">{service.title}</h3>
                    <p className="text-sm text-[#A0AEC0] mb-3">{service.desc}</p>
                    <span className="text-xs text-[#00E5C0] font-medium px-3 py-1 rounded-full bg-[rgba(0,229,192,0.08)]">
                      {service.tag}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHY CLIENTS PAY SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Clients{' '}
              <span className="text-[#00E5C0]">Happily Pay for This</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                icon: TrendUp,
                title: 'They see results immediately',
                desc: 'Leads get answered. Jobs get booked. Follow-ups happen. Clients notice the difference in the first week.'
              },
              {
                icon: Handshake,
                title: 'It replaces a hire they cannot afford',
                desc: 'A part-time admin costs $2,000+/month. Your AI service costs a fraction and works 24/7.'
              },
              {
                icon: Lock,
                title: 'They do not want to figure this out themselves',
                desc: 'Service businesses want someone to handle the tech. You are that someone.'
              },
              {
                icon: ChartLine,
                title: 'The ROI is obvious',
                desc: 'One extra booked job per week at $500 is $26,000/year. Your fee is a rounding error.'
              }
            ].map((reason, idx) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-xl p-5 border border-[rgba(255,255,255,0.05)] flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <reason.icon size={20} className="text-[#00E5C0]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{reason.title}</h3>
                  <p className="text-sm text-[#A0AEC0]">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          POSITIONING LINE SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center"
          >
            <div className="glass rounded-2xl p-10 border border-[rgba(0,229,192,0.2)] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,229,192,0.04)] to-transparent" />
              <div className="relative z-10">
                <Quotes size={32} className="text-[#00E5C0] mx-auto mb-4 opacity-40" />
                <p className="text-2xl sm:text-3xl font-bold text-white leading-snug mb-4">
                  You are not selling software.<br />
                  <span className="text-[#00E5C0]">You are selling a business that runs itself.</span>
                </p>
                <p className="text-[#A0AEC0] max-w-lg mx-auto">
                  Position yourself as the expert who makes AI work for real businesses. Not the person who sets up another SaaS tool.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          AGENCY SALES MESSAGING BLOCK
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
              <Megaphone size={14} className="text-[#00E5C0]" />
              <span className="text-xs text-[#A0AEC0]">Ready-to-Use Sales Copy</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How to Pitch This to{' '}
              <span className="text-[#00E5C0]">Your Clients</span>
            </h2>
            <p className="text-[#A0AEC0] max-w-2xl mx-auto">
              Use these proven messaging angles to land your first 10 clients.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                angle: 'The "Missing Leads" Angle',
                message: '"Right now, every lead that hits your website after hours is gone. What if every single one got a response in under 60 seconds - automatically?"',
                best_for: 'Trades, home services, contractors'
              },
              {
                angle: 'The "Hidden Revenue" Angle',
                message: '"You have 500+ past customers who have not heard from you in 6 months. What if we reactivated just 5% of them next month?"',
                best_for: 'Any service business with history'
              },
              {
                angle: 'The "Replace the Admin" Angle',
                message: '"You are paying someone $2,000/month to answer phones and chase follow-ups. What if that happened automatically for a fraction of the cost?"',
                best_for: 'Growing businesses with staff costs'
              },
              {
                angle: 'The "Competitor Edge" Angle',
                message: '"The plumber down the road responds to leads in 2 hours. Your AI responds in 30 seconds. Who do you think gets the job?"',
                best_for: 'Competitive local markets'
              }
            ].map((block, idx) => (
              <motion.div
                key={block.angle}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
              >
                <h3 className="text-[#00E5C0] font-semibold text-sm mb-3">{block.angle}</h3>
                <p className="text-white text-sm leading-relaxed mb-4 italic">
                  {block.message}
                </p>
                <div className="flex items-center gap-2">
                  <Target size={14} className="text-[#7D8896]" />
                  <span className="text-xs text-[#7D8896]">Best for: {block.best_for}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PARTNER PRO SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for Agencies{' '}
              <span className="text-[#00E5C0]">Scaling AI Services</span>
            </h2>

            <div className="glass rounded-2xl p-8 border border-[rgba(0,229,192,0.2)] mt-8">
              <div className="grid sm:grid-cols-2 gap-4 text-left mb-8">
                {[
                  "Manage multiple clients in one place",
                  "Reuse proven systems across clients",
                  "Control access and environments",
                  "Scale without increasing delivery time"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#10B981] flex-shrink-0" />
                    <span className="text-[#E2E8F0]">{feature}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold rounded-xl transition-colors"
              >
                Start Building for Clients
                <ArrowRight size={18} />
              </Link>
              
              <p className="text-sm text-[#7D8896] mt-4">
                Contact sales for enterprise scaling.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FAQ SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "Is this a course or software?",
                a: "This is software for consulting firms. It's a platform you use to deliver real systems for clients, not a training program."
              },
              {
                q: "Can I white-label reports?",
                a: "Yes. Generate branded reports for clients with your own logo and styling."
              },
              {
                q: "Do I need technical skills?",
                a: "No. If you can use email and a spreadsheet, you can use Man & Machine."
              },
              {
                q: "Can I manage multiple clients?",
                a: "Yes. Each client gets their own isolated workspace. Manage them all from one place."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.05)]"
              >
                <div className="flex items-start gap-3">
                  <Question size={20} className="text-[#00E5C0] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                    <p className="text-[#A0AEC0] text-sm">{faq.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Start Delivering AI{' '}
              <span className="text-[#00E5C0]">for Your Clients</span>
            </h2>
            
            <p className="text-lg text-[#A0AEC0] mb-10">
              Launch faster, deliver real results, and grow your agency with AI.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] text-lg font-semibold rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
              >
                Start Building for Clients
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-lg font-semibold rounded-xl hover:bg-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] transition-all"
              >
                <Calendar size={20} />
                Book a Demo
              </Link>
            </div>
            
            <p className="text-sm text-[#7D8896]">
              No credit card required. Start in under 2 minutes.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConsultantLanding;
